//---------------------------------------------------------
// Imports
//---------------------------------------------------------
import {QueryClient, useQuery} from '@tanstack/react-query';
import {currency_atom, exchange_name_atom} from 'atoms';
import {get_refresh_time, get_ticker} from 'common';
import {query_current_price, query_market_info, query_market_list} from 'connector/market';
import {useEffect, useState} from 'react';
import {useRecoilValue} from 'recoil';
import {useAuth} from './userHooks';

//---------------------------------------------------------
// Global Instance
//---------------------------------------------------------
export const queryClient = new QueryClient();
const refresh_time = get_refresh_time();

//---------------------------------------------------------
// Functions
//---------------------------------------------------------
export function usePrice(exchange: string, ticker: string, disabled?: boolean) {
	const {is_logged_in} = useAuth();

	const {data, isFetching} = useQuery({
		queryKey: ['price', `${exchange}/${ticker}`] as [string, string],
		queryFn: async (params: any) => await query_current_price(params),
		retry: (failureCount, error) => (error as any).status !== 404 && failureCount < 3,
		retryDelay: 3000,

		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		refetchIntervalInBackground: false,

		staleTime: refresh_time,

		refetchInterval: disabled ? false : refresh_time,
		enabled: is_logged_in,
	});

	return {...data, is_fetching: isFetching};
}

export function useMarketInfo(exchange: string, ticker: string) {
	const {is_logged_in} = useAuth();

	const {data, isFetching} = useQuery({
		queryKey: ['market', `${exchange}/${ticker}`] as [string, string],
		queryFn: async (params: any) => await query_market_info(params),
		retry: (failureCount, error) => (error as any).status !== 404 && failureCount < 3,
		retryDelay: 3000,

		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		refetchIntervalInBackground: false,

		staleTime: 5000,

		enabled: is_logged_in,
	});

	return {market_info: data, is_fetching: isFetching};
}

export function useMarketList() {
	const {is_logged_in} = useAuth();

	const exchange = useRecoilValue(exchange_name_atom);
	const currency = useRecoilValue(currency_atom);

	const {data, isFetching} = useQuery({
		queryKey: ['market_list', exchange] as [string, string],
		queryFn: async (params: any) => await query_market_list(params),
		retry: (failureCount, error) => (error as any).status !== 404 && failureCount < 3,
		retryDelay: 3000,

		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		refetchIntervalInBackground: false,

		staleTime: Infinity,

		enabled: is_logged_in,
	});

	const [crypto_list, set_crypto_list] = useState<string[]>([]);

	useEffect(() => {
		if (data) {
			if (exchange === 'binance') {
				const filtered_data = data.filter((item: any) => item.quote_asset.includes(currency));
				const tickers = filtered_data.map((item: any) => item.base_asset);
				set_crypto_list(tickers);
			} else {
				const tickers = data.map((item: any) => get_ticker(exchange, item.market));
				set_crypto_list(tickers);
			}
		} else set_crypto_list([]);
	}, [data, exchange, currency]);

	return {market_list: data ?? [], crypto_list, is_fetching: isFetching};
}
